// One capture listener chooses the visible layer before any close can unmount it.
// DOM order breaks equal-z-index ties, including child-first React effects.
const layers = new Set();
const focusable = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function trapLayerTab(event, element) {
  const items = Array.from(element?.querySelectorAll(focusable) || []).filter((el) => el.offsetParent !== null && el.tabIndex >= 0);
  if (!items.length) { event.preventDefault(); return; }
  const first = items[0], last = items[items.length - 1];
  const active = element.ownerDocument.activeElement;
  if (!element.contains(active) || (event.shiftKey ? active === first : active === last)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
  }
}

function orderedLayers() {
  const visible = [...layers].filter((layer) => layer.element()?.isConnected);
  // Nested dialogs inherit the outer stacking context. Compare their enclosing
  // layers first, so a child's local z-index cannot outrank another outer layer.
  const chain = (layer) => visible.filter((parent) => parent.element().contains(layer.element()))
    .sort((a, b) => a === b ? 0 : a.element().contains(b.element()) ? -1 : 1);
  return visible.sort((a, b) => {
    const ac = chain(a), bc = chain(b);
    let i = 0;
    while (ac[i] && ac[i] === bc[i]) i++;
    if (!ac[i] || !bc[i]) return bc.length - ac.length;
    if (ac[i].zIndex !== bc[i].zIndex) return bc[i].zIndex - ac[i].zIndex;
    return ac[i].element().compareDocumentPosition(bc[i].element()) & 4 ? 1 : -1;
  });
}

function onKey(event) {
  if (event.defaultPrevented || !['Escape', 'Tab', 'Enter'].includes(event.key)) return;
  const ordered = orderedLayers();
  const top = ordered[0];
  if (!top) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!event.repeat) top.onEscape();
  } else if (event.key === 'Tab') {
    // A portaled menu returns focus to its trigger. Then the enclosing dialog
    // can apply its boundary trap before the browser performs the normal Tab.
    const anchor = top.onTab?.(event);
    if (anchor) ordered.find((layer) => layer !== top && layer.element()?.contains(anchor))?.onTab?.(event);
  } else if (event.key === 'Enter') {
    top.onEnter?.(event);
  }
}

export function registerKeyboardLayer(layer) {
  if (!layers.size) document.addEventListener('keydown', onKey, true);
  layers.add(layer);
  return () => {
    layers.delete(layer);
    if (!layers.size) document.removeEventListener('keydown', onKey, true);
  };
}
